import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GraduationCap, Star, Globe, Clock, Video, Loader2, AlertCircle, CalendarClock, Volume2, Languages } from "lucide-react";
import axios from "axios";
import { createCheckout } from "../lib/api";
import { Tx } from "../context/AutoTranslateContext";
import { useLanguage } from "../context/LanguageContext";
import TranslationNotice from "../components/TranslationNotice";
import { convertSlotToLocal, DAY_NAMES, localTzName } from "../lib/timezone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TutorDetail = () => {
  const { tutorId } = useParams();
  const { lang } = useLanguage();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [prices, setPrices] = useState({ single: 8, monthly_4: 25, monthly_8: 40 });

  const [name, setName] = useState("");
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("icc_academy_email") || ""; } catch { return ""; }
  });
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("Tajweed — 30-min 1-to-1");
  const [plan, setPlan] = useState("monthly_4");
  const [startsAt, setStartsAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);   // {attempts, best_score, tutor_booking_unlocked}
  const [recitation, setRecitation] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);

  const loadRecitation = async () => {
    if (recitation) return;
    setLoadingRec(true);
    try {
      const r = await axios.get(`${API}/tutoring/tutors/${tutorId}/recitation`);
      setRecitation(`data:audio/webm;base64,${r.data.audio_b64}`);
    } catch (e) { console.debug("no recitation:", e); }
    finally { setLoadingRec(false); }
  };

  const checkProgress = async (e) => {
    const val = (e?.target?.value ?? email).trim();
    if (!val) { setProgress(null); return; }
    try {
      const r = await axios.get(`${API}/academy/progress`, { params: { email: val } });
      setProgress(r.data);
    } catch { setProgress(null); }
  };

  useEffect(() => { if (email) checkProgress(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    axios.get(`${API}/tutoring/tutors/${tutorId}`)
      .then((r) => { setT(r.data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
    axios.get(`${API}/tutoring/prices`).then((r) => {
      const map = {};
      (r.data.plans || []).forEach((p) => { map[p.id] = p.price_gbp; });
      setPrices((cur) => ({ ...cur, ...map }));
    }).catch(() => {});
  }, [tutorId]);

  const book = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const iso = new Date(startsAt).toISOString();
      // Persist email so pronunciation coach picks it up too.
      try { localStorage.setItem("icc_academy_email", email.trim()); } catch (e) { console.debug("email persist failed:", e); }
      const { data } = await axios.post(`${API}/tutoring/sessions/book`, {
        tutor_id: tutorId,
        student_name: name.trim(),
        student_email: email.trim(),
        student_phone: phone.trim(),
        starts_at: iso,
        plan,
        subject: subject.trim(),
        preferred_lang: lang,
        student_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const co = await createCheckout({
        product_type: "tutor_session",
        tutor_session_id: data.session_id,
        full_name: name.trim(),
        email: email.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = co.url;
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not book session");
      setBusy(false);
    }
  };

  if (loading) return <div className="text-center py-24 text-mute"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (notFound) return (
    <div className="max-w-md mx-auto px-4 py-24 text-center">
      <p className="text-mute mb-4"><Tx>Tutor not found or not approved yet.</Tx></p>
      <Link to="/academy/tutors" className="text-forest hover:underline"><Tx>← Back to tutors</Tx></Link>
    </div>
  );

  const price1 = Number(prices.single);
  const price4 = Number(prices.monthly_4);
  const price8 = Number(prices.monthly_8);
  const fee = plan === "single" ? price1 : plan === "monthly_4" ? price4 : price8;
  const numSessions = plan === "single" ? 1 : plan === "monthly_4" ? 4 : 8;

  return (
    <div data-testid="tutor-detail-page" className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link>
        <span>/</span>
        <Link to="/academy/tutors" className="hover:text-forest"><Tx>Tutors</Tx></Link>
        <span>/</span>
        <span className="text-forest">{t.full_name}</span>
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-8">
        <aside>
          <div className="clay-surface rounded-3xl p-6">
            <div className="w-24 h-24 rounded-full bg-forest/10 text-forest flex items-center justify-center mx-auto mb-3 overflow-hidden">
              {t.photo_url ? <img src={t.photo_url} alt="" className="w-full h-full object-cover" /> : <GraduationCap className="w-10 h-10" />}
            </div>
            <h1 className="text-center font-heading text-xl text-forest font-bold">{t.full_name}</h1>
            <p className="text-center text-[10px] uppercase tracking-widest text-mute mt-1 flex items-center justify-center gap-1">
              <Globe className="w-3 h-3" /> {t.country}
            </p>
            {t.rating_avg != null && (
              <p className="text-center text-sm font-semibold text-gold mt-2 flex items-center justify-center gap-1">
                <Star className="w-4 h-4 fill-current" /> {t.rating_avg.toFixed(1)}
                <span className="text-mute font-normal">({t.rating_count})</span>
              </p>
            )}
            <div className="border-t border-forest/10 mt-4 pt-4 text-xs text-mute space-y-2">
              <div>
                <span className="text-forest font-semibold">£{price1}</span> — <Tx>one 30-min lesson</Tx>
              </div>
              <div>
                <span className="text-forest font-semibold">£{price4}</span> — <Tx>four 30-min lessons over 1 month</Tx>
              </div>
              <div>
                <span className="text-forest font-semibold">£{price8}</span> — <Tx>eight 30-min lessons over 1 month</Tx>
              </div>
              <div>{t.experience_years} <Tx>years experience</Tx></div>
              {t.ijaza && <div className="italic">📜 {t.ijaza}</div>}
            </div>
            <div className="border-t border-forest/10 mt-4 pt-4">
              <p className="text-[10px] uppercase tracking-widest text-gold mb-2"><Tx>Teaches</Tx></p>
              <div className="flex flex-wrap gap-1.5">
                {(t.specialities || []).map((s) => (
                  <span key={s} className="text-[10px] uppercase tracking-widest bg-forest/10 text-forest px-2 py-0.5 rounded-full">{s}</span>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <p className="text-[10px] uppercase tracking-widest text-gold mb-2"><Tx>Languages</Tx></p>
              <div className="flex flex-wrap gap-1.5">
                {(t.languages || []).map((l) => (
                  <span key={l} className="text-[10px] bg-cream text-mute px-2 py-0.5 rounded-full border border-forest/10">{l}</span>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <main>
          <div className="clay-surface rounded-3xl p-6 mb-6">
            <p className="text-[10px] uppercase tracking-widest text-gold mb-2"><Tx>About</Tx></p>
            <p className="text-sm text-ink/85 leading-relaxed whitespace-pre-line">{t.bio}</p>
          </div>

          {/* Recitation sample — public only after admin approval */}
          {t.has_recitation && (
            <div data-testid="tutor-recitation" className="clay-surface rounded-3xl p-6 mb-6">
              <p className="text-[10px] uppercase tracking-widest text-gold mb-2 flex items-center gap-1.5">
                <Volume2 className="w-3.5 h-3.5" /> <Tx>Listen to a recitation sample</Tx>
              </p>
              {!recitation ? (
                <button data-testid="play-recitation" onClick={loadRecitation} disabled={loadingRec}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-sand text-xs hover:bg-sage disabled:opacity-60">
                  {loadingRec ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                  <Tx>Play sample</Tx>
                </button>
              ) : (
                <audio data-testid="recitation-audio" src={recitation} controls autoPlay className="w-full mt-1" />
              )}
            </div>
          )}

          {/* Live AI translation reassurance — BEFORE booking */}
          <TranslationNotice className="mb-6" />

          <form onSubmit={book} className="clay-surface rounded-3xl p-6 space-y-4" data-testid="booking-form">
            <div className="flex items-center gap-2 mb-2">
              <Video className="w-5 h-5 text-forest" />
              <h2 className="font-heading text-lg text-forest font-semibold"><Tx>Book a session</Tx></h2>
            </div>
            <p className="text-xs text-mute -mt-3">
              <Tx>All sessions are 30 minutes on a private, encrypted video-call link (Jitsi Meet). Choose a monthly plan and pick the first session's date & time — the remaining sessions are scheduled at the same weekly slot.</Tx>
            </p>

            <div className="grid sm:grid-cols-3 gap-3">
              <button type="button" data-testid="plan-single" onClick={() => setPlan("single")}
                className={`p-4 rounded-2xl border-2 text-left transition ${plan === "single" ? "border-gold bg-gold/10" : "border-forest/10 hover:border-forest/30"}`}>
                <p className="text-[10px] uppercase tracking-widest text-gold"><Tx>Try one lesson</Tx></p>
                <p className="font-heading text-2xl text-forest font-bold">£{price1}</p>
                <p className="text-xs text-mute"><Tx>One 30-minute lesson (paid one time)</Tx></p>
              </button>
              <button type="button" data-testid="plan-monthly-4" onClick={() => setPlan("monthly_4")}
                className={`p-4 rounded-2xl border-2 text-left transition ${plan === "monthly_4" ? "border-gold bg-gold/10" : "border-forest/10 hover:border-forest/30"}`}>
                <p className="text-[10px] uppercase tracking-widest text-gold"><Tx>1 lesson per week</Tx></p>
                <p className="font-heading text-2xl text-forest font-bold">£{price4}</p>
                <p className="text-xs text-mute"><Tx>Four 30-min lessons over 1 month</Tx></p>
              </button>
              <button type="button" data-testid="plan-monthly-8" onClick={() => setPlan("monthly_8")}
                className={`p-4 rounded-2xl border-2 text-left transition ${plan === "monthly_8" ? "border-gold bg-gold/10" : "border-forest/10 hover:border-forest/30"}`}>
                <p className="text-[10px] uppercase tracking-widest text-gold"><Tx>2 lessons per week</Tx></p>
                <p className="font-heading text-2xl text-forest font-bold">£{price8}</p>
                <p className="text-xs text-mute"><Tx>Eight 30-min lessons over 1 month</Tx></p>
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Your name</Tx></span>
                <input data-testid="book-name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Email</Tx></span>
                <input data-testid="book-email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={checkProgress}
                  className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>WhatsApp / phone</Tx></span>
                <input data-testid="book-phone" required value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Subject</Tx></span>
                <input data-testid="book-subject" required value={subject} onChange={(e) => setSubject(e.target.value)}
                  className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
              </label>
              <label className="block sm:col-span-2">
                <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>First session — date & time</Tx></span>
                <input data-testid="book-starts-at" type="datetime-local" required value={startsAt} onChange={(e) => setStartsAt(e.target.value)}
                  className="w-full mt-1 px-4 py-2.5 rounded-full bg-cream border border-forest/10 focus:border-forest outline-none text-sm" />
                <span className="text-[10px] text-mute mt-1 block">
                  <Tx>Remaining sessions run at the same weekday and time each week.</Tx>
                  {" "}<Tx>Times shown in your timezone:</Tx> <strong>{localTzName()}</strong>.
                </span>
              </label>
            </div>

            {/* Tutor's published availability — converted to student's local timezone */}
            {t.slots && t.slots.length > 0 && (
              <div data-testid="tutor-slots" className="rounded-2xl border-2 border-gold/30 bg-gold/5 p-4">
                <p className="text-[10px] uppercase tracking-widest text-gold mb-2 flex items-center gap-1">
                  <CalendarClock className="w-3.5 h-3.5" /> <Tx>Tutor's weekly slots (converted to your time)</Tx>
                </p>
                <div className="flex flex-wrap gap-2">
                  {t.slots.map((s) => {
                    const conv = convertSlotToLocal(s.weekday, s.local_time, s.tz);
                    return (
                      <button key={s.id} type="button" data-testid={`slot-${s.id}`}
                        onClick={() => setStartsAt(conv.utcIso ? conv.utcIso.slice(0, 16) : "")}
                        className="text-xs px-3 py-1.5 rounded-full bg-white border border-gold/40 text-forest hover:bg-gold/10">
                        <strong>{conv.label}</strong>
                        <span className="text-mute ml-1">({DAY_NAMES[s.weekday]} {s.local_time} {s.tz.split("/").pop()})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Learning-flow gate — spec 2026-02 */}
            {progress && !progress.tutor_booking_unlocked && (
              <div data-testid="booking-gate" className="rounded-2xl p-4 bg-gold/10 border-2 border-gold/40 text-sm">
                <p className="font-semibold text-forest mb-1">
                  <Tx>One more step before you can book</Tx>
                </p>
                <p className="text-xs text-mute mb-2">
                  <Tx>To keep our tutors' time protected, every student first completes the free AI Pronunciation Coach and passes at least one attempt. It only takes a few minutes.</Tx>
                </p>
                <Link to="/academy/pronunciation" data-testid="gate-to-pronunciation"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest text-sand text-xs hover:bg-sage">
                  <Tx>Start the free AI trial →</Tx>
                </Link>
              </div>
            )}

            <div className="bg-forest text-sand rounded-2xl p-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gold">
                  <Tx>You will pay</Tx>
                </p>
                <p className="font-heading text-2xl font-bold">£{fee}</p>
                <p className="text-[11px] text-sand/75">
                  <Tx>for</Tx> {numSessions} × 30-<Tx>min live lesson(s)</Tx>
                </p>
              </div>
              <button
                data-testid="book-submit"
                type="submit"
                disabled={busy || (progress && !progress.tutor_booking_unlocked)}
                className="bg-gold text-forest rounded-full px-6 py-3 text-sm font-medium hover:bg-sand transition disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Tx>Pay £</Tx>{fee} <Tx>& confirm booking</Tx></>}
              </button>
            </div>

            {error && (
              <div data-testid="book-error" className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{error}</span>
              </div>
            )}
            <p className="text-[11px] text-mute italic">
              <Clock className="w-3 h-3 inline mr-1" />
              <Tx>Refund policy: full refund up to 24h before the session. Within 24h — emergency refunds are handled by the admin on request.</Tx>
            </p>
          </form>

          {t.recent_ratings?.length > 0 && (
            <div className="clay-surface rounded-3xl p-6 mt-6">
              <p className="text-[10px] uppercase tracking-widest text-gold mb-3"><Tx>Recent ratings</Tx></p>
              <div className="space-y-3">
                {t.recent_ratings.map((r, i) => (
                  <div key={i} className="border-b border-forest/10 pb-3 last:border-0 last:pb-0">
                    <p className="text-sm font-semibold text-gold flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-current" /> {r.rating}/5
                    </p>
                    {r.comment && <p className="text-xs text-mute mt-1 italic">"{r.comment}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TutorDetail;
